import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import axiosConfiguration from "./configuration/axios";
import { InputNumber } from "primereact/inputnumber";
import { DateTime } from "luxon";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";

interface CustomerInformation {
    name: string;
    lastName: string;
    street: string;
    zipCode: string;
    city: string;
    country: string;
    company: string;
    addressSupplement: string;
}

interface ShippingServiceProvider {
    kVersandArt: number;
    cName: string;
}

export default function TrackingModule() {
    let location = useNavigate();

    const [loading, setLoading] = useState(true);
    const [orderKey, setOrderKey] = useState("");
    const [orderNumber, setOrderNumber] = useState("");
    const [deliveryNoteID, setDeliveryNoteID] = useState(0);
    const [orderDate, setOrderDate] = useState<null | Date>(null);
    const [deliveryNote, setDeliveryNote] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [shippingServiceProviders, setShippingServiceProviders] = useState<
        ShippingServiceProvider[]
    >([]);
    const [
        selectedShippingServiceProvider,
        setSelectedShippingServiceProvider,
    ] = useState<ShippingServiceProvider | null>(null);
    const [customerInformation, setCustomerInformation] =
        useState<CustomerInformation | null>(null);

    const [trackingNumbers, setTrackingNumbers] = useState([""]);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const toast = useRef(null);

    const validateUrl = async () => {
        const orderkey = getOrderKeyFromUrl();
        if (!orderkey) {
            location("/", { replace: true });
            return;
        }
        loadFormInformation(orderkey);
    };

    const getOrderKeyFromUrl = (): null | string => {
        return searchParams.get("orderkey");
    };

    const loadFormInformation = async (orderKey: string) => {
        try {
            await getShippingServiceProviders(orderKey);
            setOrderKey(orderKey);
            setLoading(false);
        } catch (err) {}
    };

    const getShippingServiceProviders = async (orderKey: string) => {
        await axiosConfiguration
            .get(`/order?orderkey=${orderKey}`)
            .then((response) => {
                setShippingServiceProviders(response.data.shipping);
                setCustomerInformation(response.data.address);
                setOrderNumber(response.data.basic.orderNumber);
                setOrderDate(new Date(response.data.basic.orderDate));
                setDeliveryNote(response.data.basic.deliveryNote);
                setDeliveryNoteID(response.data.id);
            })
            .catch((err) => {
                location("/", { replace: true });
                return;
            });
    };

    const changeTrackingNumberCount = async (newCount: number) => {
        let oldTrackingNumbers = [...trackingNumbers];

        if (newCount > oldTrackingNumbers.length) {
            oldTrackingNumbers.push("");
        } else if (
            newCount < oldTrackingNumbers.length &&
            oldTrackingNumbers.length > 1
        ) {
            oldTrackingNumbers.pop();
        }
        await setTrackingNumbers(oldTrackingNumbers);
    };

    const checkIfTrackingCodesAreFilled = () => {
        let allFilled = true;
        const trackingNumbers_ = [...trackingNumbers];

        for (let i = 0; i < trackingNumbers_.length; i++) {
            if (trackingNumbers_[i] === "") {
                allFilled = false;
                break;
            } else if (trackingNumbers_[i].length < 10) {
                allFilled = false;
                break;
            }
        }

        return allFilled;
    };

    useEffect(() => {
        validateUrl();
    }, []);

    return (
        <>
            <Toast ref={toast} />

            {loading ? (
                <div className="flex">
                    <ProgressSpinner className="m-auto" />
                </div>
            ) : (
                <>
                    <header className="topbar mb-4">
                        <img
                            id="logo"
                            src="/studioexpresslogo.jpg"
                            alt="logo"
                        />
                    </header>
                    <div className="main-container">
                        <div className="grid mr-0 ml-0">
                            <div className="col 12 lg:col-10 lg:col-offset-1 xl:col-6 xl:col-offset-3">
                                <Card
                                    title={
                                        orderNumber !== ""
                                            ? `BESTELLUNG: ${orderNumber} / LIEFERSCHEIN: ${deliveryNote}`
                                            : `LIEFERSCHEIN: ${deliveryNote}`
                                    }
                                    subTitle={`-- Bestelldatum: 
                                        ${
                                            orderDate
                                                ? DateTime.fromJSDate(orderDate)
                                                      .minus({ hours: 2 })
                                                      .toFormat(
                                                          "dd.MM.yyyy HH:mm:ss"
                                                      )
                                                : ""
                                        }`}
                                >
                                    <div className="grid">
                                        <div className="col-12">
                                            <div>
                                                <div className="font-medium text-xl text-gray-600 mb-2 mt-3">
                                                    <i className="pi pi-home mr-1 text-xl"></i>
                                                    Addresse:
                                                </div>
                                                <ul className="list-none p-0 m-0">
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Name
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.name
                                                            }{" "}
                                                            {
                                                                customerInformation?.lastName
                                                            }
                                                        </div>
                                                    </li>
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Firma
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.company
                                                            }
                                                        </div>
                                                    </li>
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Adresszusatz
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.addressSupplement
                                                            }
                                                        </div>
                                                    </li>
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Straße
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.street
                                                            }
                                                        </div>
                                                    </li>
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Stadt
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.zipCode
                                                            }
                                                            ,{" "}
                                                            {
                                                                customerInformation?.city
                                                            }
                                                        </div>
                                                    </li>
                                                    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
                                                        <div className="text-500 w-6 lg:w-3 md:w-2 font-medium">
                                                            Land
                                                        </div>
                                                        <div className="text-900 w-full lg:w-7 md:w-8 md:flex-order-0 flex-order-1">
                                                            {
                                                                customerInformation?.country
                                                            }
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="font-medium text-xl text-gray-600 mb-2 mt-3">
                                                <i className="pi pi-car mr-1 text-xl"></i>
                                                Versanddienstleister:
                                            </div>

                                            <Dropdown
                                                options={
                                                    shippingServiceProviders
                                                }
                                                value={
                                                    selectedShippingServiceProvider
                                                }
                                                onChange={(e) =>
                                                    setSelectedShippingServiceProvider(
                                                        e.value
                                                    )
                                                }
                                                optionLabel="cName"
                                                placeholder="Versanddienstleister auswählen"
                                                className="w-full"
                                            />
                                            <div className="font-medium text-xl text-gray-600 mb-2 mt-3">
                                                <i className="pi pi-filter mr-1 text-xl"></i>
                                                Anzahl der Pakete:
                                            </div>
                                            <InputNumber
                                                inputId="horizontal"
                                                value={trackingNumbers.length}
                                                disabled={
                                                    !selectedShippingServiceProvider
                                                }
                                                className="w-full"
                                                min={1}
                                                onValueChange={async (e) => {
                                                    if (e.value) {
                                                        await changeTrackingNumberCount(
                                                            e.value
                                                        );
                                                    }
                                                }}
                                                showButtons
                                                buttonLayout="horizontal"
                                                step={1}
                                                decrementButtonClassName="p-button-danger"
                                                incrementButtonClassName="p-button-success"
                                                incrementButtonIcon="pi pi-plus"
                                                decrementButtonIcon="pi pi-minus"
                                            />
                                            <div className="font-medium text-xl text-gray-600 mb-2 mt-3">
                                                <i className="pi pi-book mr-1 text-xl"></i>
                                                Trackingnummer(n):
                                            </div>
                                            {trackingNumbers.map(
                                                (trackingNumber, index) => (
                                                    <InputText
                                                        key={index}
                                                        placeholder={`Trackingnummer eingeben (${
                                                            index + 1
                                                        })`}
                                                        className="w-full mb-1"
                                                        value={trackingNumber}
                                                        disabled={
                                                            !selectedShippingServiceProvider
                                                        }
                                                        onChange={(e) => {
                                                            let oldTrackingNumbers =
                                                                [
                                                                    ...trackingNumbers,
                                                                ];
                                                            oldTrackingNumbers[
                                                                index
                                                            ] = e.target.value;
                                                            setTrackingNumbers(
                                                                oldTrackingNumbers
                                                            );
                                                        }}
                                                    />
                                                )
                                            )}
                                            <div className="mt-2">
                                                <Button
                                                    className="w-full"
                                                    icon="pi pi-link"
                                                    label="Aktualisieren"
                                                    disabled={
                                                        !checkIfTrackingCodesAreFilled()
                                                    }
                                                    onClick={() =>
                                                        setConfirmDialog(true)
                                                    }
                                                />
                                                <ConfirmDialog
                                                    acceptLabel="Ja"
                                                    rejectLabel="Nein"
                                                    visible={confirmDialog}
                                                    onHide={() =>
                                                        setConfirmDialog(false)
                                                    }
                                                    className="w-4"
                                                    message="Die Trackingnummern können pro Auftrag nur einmal übermittelt werden. Sobald Sie die Daten übermittelt haben wird diese Seite gelöscht. Stellen Sie sicher, dass die Informationen korrekt sind!"
                                                    header="Bestätigung"
                                                    icon="pi pi-exclamation-triangle"
                                                    accept={async () => {
                                                        try {
                                                            await axiosConfiguration.post(
                                                                `/order?trackingNumbers=${JSON.stringify(
                                                                    trackingNumbers
                                                                )}&shippingServiceProvider=${
                                                                    selectedShippingServiceProvider?.kVersandArt
                                                                }&deliveryNoteID=${deliveryNoteID}`
                                                            );
                                                            //@ts-ignore
                                                            toast.current.show({
                                                                severity:
                                                                    "info",
                                                                summary:
                                                                    "Daten übermittelt",
                                                                detail: "Trackingnummer(n) werden aktualisiert!",
                                                                life: 3000,
                                                            });
                                                            setTimeout(
                                                                function () {
                                                                    location(
                                                                        "/",
                                                                        {
                                                                            replace:
                                                                                true,
                                                                        }
                                                                    );
                                                                    return;
                                                                },
                                                                3000
                                                            );
                                                        } catch (err: any) {
                                                            if (
                                                                err.response
                                                                    .status ===
                                                                400
                                                            ) {
                                                                //@ts-ignore
                                                                toast.current.show(
                                                                    {
                                                                        severity:
                                                                            "warn",
                                                                        summary:
                                                                            "Es wurden keine Daten übertragen",
                                                                        detail: "Trackingnummer(n) wurden nicht aktualisiert!",
                                                                        life: 3000,
                                                                    }
                                                                );
                                                            } else {
                                                                //@ts-ignore
                                                                toast.current.show(
                                                                    {
                                                                        severity:
                                                                            "error",
                                                                        summary:
                                                                            "Bitte versuchen Sie es später erneut",
                                                                        detail: "Trackingnummer(n) wurden nicht aktualisiert!",
                                                                        life: 3000,
                                                                    }
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
